import React, { useRef, useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import LoadingWheelSmall from "../../components/LoadingWheelSmall";
import TimePickerModal from "../../components/TimePickerModal";
import DownloadFilenameModal from "../../components/DownloadFilenameModal";
import Modal from "../../components/Modal";
import { lotService, lotDailyDataService } from "../../utils/api";
import "./DashboardHeader.css";
import { queryClient, cacheKeys } from "../../utils/queryClient";
import Menu, { type MenuItem } from "../../components/Menu";
import { useMultiLot } from "../../contexts/MultiLotContext";

interface DashboardHeaderProps {
    lotName: string;
    isLoading: boolean;
    timeframe: "day" | "week" | "month" | "year";
    setTimeframe: (timeframe: "day" | "week" | "month" | "year") => void;
    onDateRangeChange: (currentRange: { startDate: string; endDate: string }, previousRange: { startDate: string; endDate: string }) => void;
    graphData?: any[];
    selectedDate: Date;
    setSelectedDate: (date: Date) => void;
    showToday: boolean;
    setShowToday: (value: boolean) => void;
}

type AnalyticsType = "revenue" | "occupancy" | "segmentation" | "emissions" | "dwell-time";

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
    lotName,
    isLoading,
    timeframe,
    setTimeframe,
    onDateRangeChange,
    graphData,
    selectedDate,
    setSelectedDate,
    showToday,
    setShowToday
}) => {
    const [isCompact, setIsCompact] = useState(false);
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [isNoDataModalOpen, setIsNoDataModalOpen] = useState(false);
    const timeframeRef = useRef<HTMLDivElement>(null);
    const analyticsRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { lotId } = useParams<{ lotId: string }>();
    const { isMultiLotMode, getDisplayLotName, getPrimaryLot } = useMultiLot();
    const [displayLotName, setDisplayLotName] = useState(lotName);
    const [prevTimeframe, setPrevTimeframe] = useState<"day" | "week" | "month" | "year">(timeframe);
    const [lastPickerSelection, setLastPickerSelection] = useState<{ date: Date, timeframe: "day" | "week" | "month" | "year" } | null>(null);
    const [canNavigateForward, setCanNavigateForward] = useState(true);
    const [companyName, setCompanyName] = useState<string | null>(null);
    const [isAnalyticsMenuOpen, setIsAnalyticsMenuOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

    // Memoize the date range calculation
    const dateRanges = useMemo(() => {
        const startDate = new Date(selectedDate);
        const endDate = new Date(selectedDate);
        const prevStartDate = new Date(selectedDate);
        const prevEndDate = new Date(selectedDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (timeframe) {
            case "day":
                // For current day - set both start and end to the same day
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(0, 0, 0, 0);
                // For previous day
                prevStartDate.setDate(selectedDate.getDate() - 1);
                prevStartDate.setHours(0, 0, 0, 0);
                prevEndDate.setDate(selectedDate.getDate() - 1);
                prevEndDate.setHours(0, 0, 0, 0);
                break;
            case "week":
                if (showToday) {
                    // For "This Week" view
                    // Set end date to today
                    endDate.setTime(today.getTime());
                    // Set start date to 6 days before today
                    startDate.setTime(today.getTime());
                    startDate.setDate(today.getDate() - 6);

                    // For previous week
                    prevEndDate.setTime(startDate.getTime());
                    prevEndDate.setDate(startDate.getDate() - 1);
                    prevStartDate.setTime(prevEndDate.getTime());
                    prevStartDate.setDate(prevEndDate.getDate() - 6);
                } else {
                    // For selected week
                    const selectedDateCopy = new Date(selectedDate);
                    selectedDateCopy.setHours(0, 0, 0, 0);

                    // If selected date is within the last 7 days, show the 7 most recent days
                    if (selectedDateCopy >= new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000) && selectedDateCopy <= today) {
                        endDate.setTime(today.getTime());
                        startDate.setTime(today.getTime());
                        startDate.setDate(today.getDate() - 6);
                    } else {
                        // For other dates, show the week starting from selected date
                        startDate.setTime(selectedDate.getTime());
                        endDate.setTime(selectedDate.getTime());
                        endDate.setDate(selectedDate.getDate() + 6);
                    }

                    // For previous week
                    prevStartDate.setTime(startDate.getTime());
                    prevStartDate.setDate(startDate.getDate() - 7);
                    prevEndDate.setTime(startDate.getTime());
                    prevEndDate.setDate(startDate.getDate() - 1);
                }
                break;
            case "month":
                // For current month - start on 1st and end on last day
                startDate.setDate(1);
                startDate.setHours(0, 0, 0, 0);

                // Hardcode month lengths
                const monthLengths: { [key: number]: number } = {
                    0: 31,  // January
                    1: 28,  // February (not handling leap years for simplicity)
                    2: 31,  // March
                    3: 30,  // April
                    4: 31,  // May
                    5: 30,  // June
                    6: 31,  // July
                    7: 31,  // August
                    8: 30,  // September
                    9: 31,  // October
                    10: 30, // November
                    11: 31  // December
                };

                // Set end date to last day of current month
                endDate.setDate(monthLengths[endDate.getMonth()]);
                endDate.setHours(23, 59, 59, 999);

                // For previous month
                prevStartDate.setMonth(selectedDate.getMonth() - 1, 1); // Set to 1st of previous month
                prevStartDate.setHours(0, 0, 0, 0);
                prevEndDate.setMonth(selectedDate.getMonth() - 1, monthLengths[(selectedDate.getMonth() - 1 + 12) % 12]); // Set to last day of previous month
                prevEndDate.setHours(23, 59, 59, 999);
                break;
            case "year":
                // For current year - start on Jan 1st and end on Dec 31st
                startDate.setMonth(0, 1); // Set to January 1st
                startDate.setHours(0, 0, 0, 0);
                endDate.setMonth(11, 31); // Set to December 31st
                endDate.setHours(23, 59, 59, 999);

                // For previous year
                prevStartDate.setFullYear(selectedDate.getFullYear() - 1, 0, 1); // Set to January 1st of previous year
                prevStartDate.setHours(0, 0, 0, 0);
                prevEndDate.setFullYear(selectedDate.getFullYear() - 1, 11, 31); // Set to December 31st of previous year
                prevEndDate.setHours(23, 59, 59, 999);
                break;
        }

        // Replace the ISO string conversion with local date formatting to avoid timezone shifts
        const formatLocalDate = (d: Date) => {
            const year = d.getFullYear();
            const month = d.getMonth() + 1; // months are zero-indexed
            const day = d.getDate();
            return `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
        };

        return {
            currentRange: {
                startDate: formatLocalDate(startDate),
                endDate: formatLocalDate(endDate)
            },
            previousRange: {
                startDate: formatLocalDate(prevStartDate),
                endDate: formatLocalDate(prevEndDate)
            }
        };
    }, [timeframe, selectedDate, showToday]);

    // Only call onDateRangeChange when the date ranges actually change
    useEffect(() => {
        onDateRangeChange(dateRanges.currentRange, dateRanges.previousRange);
    }, [dateRanges, onDateRangeChange]);

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<{ timeframe: "day" | "week" | "month" | "year" }>).detail;
            if (!detail) return;
            setTimeframe(detail.timeframe);
            setSelectedDate(new Date());
            setShowToday(true);
        };
        window.addEventListener("walkthrough-set-timeframe", handler as EventListener);
        return () => window.removeEventListener("walkthrough-set-timeframe", handler as EventListener);
    }, [setTimeframe, setSelectedDate, setShowToday]);

    // Fetch lot name when component mounts or lotId changes
    useEffect(() => {
        const fetchLotName = async () => {
            if (!lotId) return;
            try {
                const lotData = await lotService.getLotById(lotId);
                // Handle both response structures
                const lotName = lotData?.data?.lotName || lotData?.lotName;
                const fetchedCompanyName = lotData?.data?.companyName || lotData?.companyName;
                if (lotName) {
                    setDisplayLotName(lotName);
                    localStorage.setItem(`lot_${lotId}_name`, lotName);
                } else {
                    // Try to get cached name if API response doesn't have lotName
                    const cachedName = localStorage.getItem(`lot_${lotId}_name`);
                    setDisplayLotName(cachedName || "Unknown Lot");
                }
                if (fetchedCompanyName) {
                    setCompanyName(fetchedCompanyName);
                } else {
                    setCompanyName(null); // Or handle as needed if companyName is expected
                }
            } catch (error) {
                // Try to get cached name if API call fails
                const cachedName = localStorage.getItem(`lot_${lotId}_name`);
                setDisplayLotName(cachedName || "Unknown Lot");
            }
        };

        fetchLotName();
    }, [lotId]);

    // Load the stored timeframe for this lot on lotId change and initialize if not present
    useEffect(() => {
        if (!lotId) return;
        const storedTimeframe = localStorage.getItem(`timeframe_${lotId}`);
        if (storedTimeframe && storedTimeframe !== timeframe) {
            setTimeframe(storedTimeframe as "day" | "week" | "month" | "year");
        } else if (!storedTimeframe) {
            localStorage.setItem(`timeframe_${lotId}`, timeframe);
        }
    }, [lotId]);

    // Update localStorage whenever the timeframe changes
    useEffect(() => {
        if (lotId) {
            localStorage.setItem(`timeframe_${lotId}`, timeframe);
        }
    }, [timeframe, lotId]);

    React.useEffect(() => {
        // In multi-lot mode, use the aggregated display name
        if (isMultiLotMode) {
            setDisplayLotName(getDisplayLotName());
        } else if (lotName && lotName !== "...") {
            setDisplayLotName(lotName);
        } else if (lotId) {
            const cachedName = localStorage.getItem(`lot_${lotId}_name`);
            if (cachedName) {
                setDisplayLotName(cachedName);
            }
        }
    }, [lotName, lotId, isMultiLotMode, getDisplayLotName]);

    // Get current analytics type from URL
    const currentAnalytics = location.pathname.split('/').pop() as AnalyticsType;

    const getFormattedDateRange = (timeframe: "day" | "week" | "month" | "year"): string => {
        const now = new Date();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

        const formatDate = (date: Date): string => {
            const day = date.getDate();
            const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3) ? 0 : (day % 100 - day % 10 != 10) ? day % 10 : 0] || 'th';
            return `${monthNames[date.getMonth()]} ${day}${suffix}`;
        };

        const isToday = (date: Date): boolean => {
            const today = new Date();
            return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
        };

        const isThisWeek = (date: Date): boolean => {
            const today = new Date();
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            const endOfWeek = new Date(today);
            endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
            return date >= startOfWeek && date <= endOfWeek;
        };

        const isThisMonth = (date: Date): boolean => {
            const today = new Date();
            return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
        };

        const isThisYear = (date: Date): boolean => {
            const today = new Date();
            return date.getFullYear() === today.getFullYear();
        };

        if (showToday) {
            switch (timeframe) {
                case 'day':
                    return `${dayNames[now.getDay()]} ${formatDate(now)}, ${now.getFullYear()} (Today)`;
                case 'week': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - 6); // 7-day window: today and previous 6 days
                    const endOfWeek = new Date(now); // end is today
                    return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}, ${now.getFullYear()} (This Week)`;
                }
                case 'month':
                    return `${monthNames[now.getMonth()]} ${now.getFullYear()} (This Month to Today)`;
                case 'year':
                    return `${now.getFullYear()} (This Year to Today)`;
                default:
                    return '';
            }
        } else {
            switch (timeframe) {
                case 'day': {
                    const dayName = dayNames[selectedDate.getDay()];
                    return `${dayName} ${formatDate(selectedDate)}, ${selectedDate.getFullYear()}`;
                }
                case 'week': {
                    const maxDate = new Date();
                    // Set time to 0 for accurate comparison
                    maxDate.setHours(0, 0, 0, 0);
                    let startOfWeek = new Date(selectedDate);
                    startOfWeek.setHours(0, 0, 0, 0);
                    const potentialEnd = new Date(selectedDate);
                    potentialEnd.setDate(selectedDate.getDate() + 6);
                    potentialEnd.setHours(0, 0, 0, 0);
                    if (potentialEnd > maxDate) {
                        // Adjust window so that it ends at maxDate
                        startOfWeek = new Date(maxDate);
                        startOfWeek.setDate(maxDate.getDate() - 6);
                        return `${formatDate(startOfWeek)} - ${formatDate(maxDate)}, ${maxDate.getFullYear()}`;
                    } else {
                        const endOfWeek = potentialEnd;
                        return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}, ${selectedDate.getFullYear()}`;
                    }
                }
                case 'month':
                    return `${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;
                case 'year':
                    return `${selectedDate.getFullYear()}`;
                default:
                    return '';
            }
        }
    };

    // Update analytics pill width and position dynamically
    useEffect(() => {
        const updateAnalyticsPill = () => {
            if (analyticsRef.current) {
                const activeButton = analyticsRef.current.querySelector('.active') as HTMLElement;
                const activePill = analyticsRef.current.querySelector('.active-pill') as HTMLElement;
                if (activeButton && activePill) {
                    const containerRect = analyticsRef.current.getBoundingClientRect();
                    const buttonRect = activeButton.getBoundingClientRect();

                    // Calculate position relative to the container
                    const leftOffset = buttonRect.left - containerRect.left;
                    const width = activeButton.offsetWidth;

                    activePill.style.left = `${leftOffset}px`;
                    activePill.style.width = `${width}px`;
                }
            }
        };

        updateAnalyticsPill();
        // Add resize observer to update on container resize or button changes
        const resizeObserver = new ResizeObserver(updateAnalyticsPill);
        if (analyticsRef.current) {
            resizeObserver.observe(analyticsRef.current);
            // Also observe the buttons themselves in case their width changes (e.g., due to text change or padding)
            analyticsRef.current.querySelectorAll('button').forEach(button => {
                resizeObserver.observe(button);
            });
        }

        return () => {
            if (analyticsRef.current) {
                resizeObserver.disconnect();
            }
        };
    }, [currentAnalytics]); // Rerun when active analytics changes

    // Update timeframe pill width and position dynamically using the label span
    useEffect(() => {
        const updateTimeframePill = () => {
            if (timeframeRef.current) {
                const activeButton = timeframeRef.current.querySelector('button.active') as HTMLElement;
                const activePill = timeframeRef.current.querySelector('.active-pill') as HTMLElement;
                if (activeButton && activePill) {
                    const labelSpan = activeButton.querySelector('.timeframe-label') as HTMLElement;
                    if (labelSpan) {
                        // Define fixed pill width based on screen width
                        const fixedWidth = isSmallScreen ? 50 : 72;
                        // Calculate the center of the label span relative to the container
                        const centerX = activeButton.offsetLeft + labelSpan.offsetLeft + (labelSpan.offsetWidth / 2);
                        // Determine the left position for the pill so that it is centered under the text
                        let finalLeft = centerX - (fixedWidth / 2);

                        // Adjust left position specifically for the "Day" pill
                        if (timeframe === 'day') {
                            finalLeft -= 2;
                        }

                        activePill.style.left = `${finalLeft}px`;
                        activePill.style.width = `${fixedWidth}px`;
                    } else {
                        // Fallback to covering the whole button if the label span is not found
                        activePill.style.left = `${activeButton.offsetLeft}px`;
                        activePill.style.width = `${activeButton.offsetWidth}px`;
                    }
                }
            }
        };

        updateTimeframePill();
        const resizeObserver = new ResizeObserver(updateTimeframePill);
        if (timeframeRef.current) {
            resizeObserver.observe(timeframeRef.current);
            timeframeRef.current.querySelectorAll('button').forEach(button => {
                resizeObserver.observe(button);
            });
        }

        // Add window resize listener to handle screen size changes
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 480);
            updateTimeframePill();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            if (timeframeRef.current) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [timeframe, isCompact, isSmallScreen]);

    // Add effect to handle timeframe selector width (Keep this as it controls isCompact)
    React.useEffect(() => {
        const checkWidth = () => {
            if (timeframeRef.current) {
                setIsCompact(timeframeRef.current.offsetWidth < 230);
            }
        };

        checkWidth();
        const resizeObserver = new ResizeObserver(checkWidth);
        if (timeframeRef.current) {
            resizeObserver.observe(timeframeRef.current);
        }

        return () => {
            if (timeframeRef.current) {
                resizeObserver.unobserve(timeframeRef.current);
            }
        };
    }, []);

    // Add this function to handle date navigation
    const navigateDate = (direction: 'forward' | 'backward') => {
        const newDate = new Date(selectedDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (timeframe) {
            case 'day':
                newDate.setDate(newDate.getDate() + (direction === 'forward' ? 1 : -1));
                break;
            case 'week': {
                let newWeekStart;
                if (direction === 'backward') {
                    if (showToday) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        // For "this week" view, the window is (today - 6) to today
                        const currentWeekStart = new Date(today);
                        currentWeekStart.setDate(today.getDate() - 6);
                        newWeekStart = new Date(currentWeekStart);
                        newWeekStart.setDate(newWeekStart.getDate() - 7);
                    } else {
                        newWeekStart = new Date(selectedDate);
                        newWeekStart.setDate(newWeekStart.getDate() - 7);
                    }
                } else { // forward
                    newWeekStart = new Date(selectedDate);
                    newWeekStart.setDate(newWeekStart.getDate() + 7);
                }
                newWeekStart.setHours(0, 0, 0, 0);
                newDate.setTime(newWeekStart.getTime());
                break;
            }
            case 'month':
                newDate.setMonth(newDate.getMonth() + (direction === 'forward' ? 1 : -1));
                break;
            case 'year':
                newDate.setFullYear(newDate.getFullYear() + (direction === 'forward' ? 1 : -1));
                break;
        }

        // Check if the new date is in the future
        const isFuture = newDate > today;
        if (isFuture) {
            setCanNavigateForward(false);
            return;
        }

        newDate.setHours(0, 0, 0, 0);
        setSelectedDate(newDate);
        setShowToday(false);
        setLastPickerSelection({ date: newDate, timeframe });
        setCanNavigateForward(true);
    };

    // Add this effect to check if forward navigation is possible
    useEffect(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const testDate = new Date(selectedDate);
        switch (timeframe) {
            case 'day':
                testDate.setDate(testDate.getDate() + 1);
                break;
            case 'week': {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nextWeekStart = new Date(selectedDate);
                nextWeekStart.setDate(nextWeekStart.getDate() + 7);
                const nextWeekEnd = new Date(nextWeekStart);
                nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);
                nextWeekEnd.setHours(0, 0, 0, 0);
                setCanNavigateForward(nextWeekEnd <= today);
                break;
            }
            case 'month':
                testDate.setMonth(testDate.getMonth() + 1);
                break;
            case 'year':
                testDate.setFullYear(testDate.getFullYear() + 1);
                break;
        }

        // Only disable forward navigation if the test date is strictly after today
        setCanNavigateForward(testDate <= today);

        testDate.setHours(0, 0, 0, 0);
    }, [selectedDate, timeframe]);

    const analyticsOptions: { id: AnalyticsType; label: string }[] = [
        { id: "revenue", label: "Revenue" },
        { id: "occupancy", label: "Occupancy" },
        { id: "segmentation", label: "Segmentation" },
        { id: "emissions", label: "Emissions" },
        { id: "dwell-time", label: "Dwell Time" },
    ];

    const handleAnalyticsChange = (analyticsType: AnalyticsType) => {
        const effectiveLotId = lotId || getPrimaryLot()?.lotId;
        if (!effectiveLotId) return;

        localStorage.setItem("lastDashboardTab", analyticsType);
        navigate(`/lot/${effectiveLotId}/dashboard/${analyticsType}`);
    };

    const getAnalyticsIcon = (type: AnalyticsType, isActive: boolean) => {
        if (type === 'dwell-time') {
            return `/assets/dashboard/${isActive ? 'Selected' : 'Unselected'}_DwellTime.svg`;
        }
        return `/assets/dashboard/${isActive ? 'Selected' : 'Unselected'}_${type.charAt(0).toUpperCase() + type.slice(1)}.svg`;
    };

    const analyticsMenuItems: MenuItem[] = useMemo(() => (
        analyticsOptions.map((option) => ({
            key: option.id,
            label: option.label,
            icon: <img src={getAnalyticsIcon(option.id, currentAnalytics === option.id)} alt={option.label} />,
            selected: currentAnalytics === option.id,
            onSelect: () => handleAnalyticsChange(option.id),
        }))
    ), [analyticsOptions, currentAnalytics, getAnalyticsIcon]);


    const handleDateSelect = (date: Date) => {
        setShowToday(false);
        setSelectedDate(date);
        setLastPickerSelection({ date, timeframe });
        setIsTimePickerOpen(false);
    };

    const getDefaultDownloadFilename = () => {
        const { currentRange } = dateRanges;
        const lotNameWithoutSpaces = lotName.replace(/\s+/g, '_');
        const today = new Date();
        const currentDate = today.toISOString().split('T')[0];

        // Check if it's a day view (same start and end date)
        if (currentRange.startDate === currentRange.endDate) {
            return `${lotNameWithoutSpaces}_${currentRange.startDate}.csv`;
        }

        // Check if it's a month view (starts on 1st and ends on last day of month)
        const startDate = new Date(currentRange.startDate);
        const endDate = new Date(currentRange.endDate);
        const isMonthView = startDate.getDate() === 1 &&
            endDate.getDate() === new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

        if (isMonthView) {
            const monthYear = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`;
            // If it's the current month, use today's date
            if (endDate.getMonth() === today.getMonth() && endDate.getFullYear() === today.getFullYear()) {
                return `${lotNameWithoutSpaces}_${monthYear}_${currentDate}.csv`;
            }
            return `${lotNameWithoutSpaces}_${monthYear}.csv`;
        }

        // Check if it's a year view (starts on Jan 1st and ends on Dec 31st)
        const isYearView = startDate.getMonth() === 0 && startDate.getDate() === 1 &&
            endDate.getMonth() === 11 && endDate.getDate() === 31;

        if (isYearView) {
            return `${lotNameWithoutSpaces}_${endDate.getFullYear()}.csv`;
        }

        // For other ranges, use the date range
        return `${lotNameWithoutSpaces}_${currentRange.startDate}_${currentRange.endDate}.csv`;
    };

    const isWalkthroughActive = (): boolean => {
        if (typeof window === "undefined") return false;
        return document.body.classList.contains("walkthrough-active");
    };

    const handleDownloadData = () => {
        if (isWalkthroughActive() && Array.isArray(graphData) && graphData.length > 0) {
            const filename = getDefaultDownloadFilename().replace(/\.(csv|xls)$/i, "");
            handleDownloadConfirm(filename, "xl");
            return;
        }
        console.log('Download Data clicked');
        console.log('graphData:', graphData);
        console.log('graphData type:', typeof graphData);
        console.log('Is Array?', Array.isArray(graphData));
        console.log('Length:', graphData?.length);

        // Check if we have any data points
        if (!graphData) {
            console.error('graphData is null or undefined');
            setIsNoDataModalOpen(true);
            return;
        }

        if (!Array.isArray(graphData)) {
            console.error('graphData is not an array:', graphData);
            setIsNoDataModalOpen(true);
            return;
        }

        if (graphData.length === 0) {
            console.error('graphData array is empty');
            setIsNoDataModalOpen(true);
            return;
        }

        console.log('Data validation passed, opening download modal');
        setIsDownloadModalOpen(true);
    };

    const handleDownloadConfirm = async (filename: string, format: 'csv' | 'xl') => {
        console.log('Download confirmed with filename:', filename, 'format:', format);

        const { currentRange } = dateRanges;

        let fullData;
        try {
            if (timeframe === 'day') {
                // For day view, fetch hourly metrics using getLotHourlyByDate
                fullData = await lotDailyDataService.getLotHourlyByDate(lotId || '', currentRange.startDate);
                // The API response for getLotHourlyByDate might be an object with a 'data' property containing the array
                if (fullData && fullData.data && Array.isArray(fullData.data)) {
                    fullData = fullData.data;
                } else if (!Array.isArray(fullData)) {
                    // If the response is not an array and doesn't have a .data array, assume it's empty or malformed
                    fullData = [];
                }
            } else {
                // For week/month/year view, fetch daily metrics
                fullData = await lotDailyDataService.getDashboardMetrics(lotId || '', currentRange.startDate, currentRange.endDate);
                // The API response for getDashboardMetrics might also be an object with a 'data' property containing the array
                if (fullData && fullData.data && Array.isArray(fullData.data)) {
                    fullData = fullData.data;
                } else if (!Array.isArray(fullData)) {
                    // If the response is not an array and doesn't have a .data array, assume it's empty or malformed
                    fullData = [];
                }
            }
        } catch (err) {
            console.error('Error fetching full data for download:', err);
            alert('Failed to fetch download data.');
            return;
        }

        // If the fetched fullData is empty, fallback to using graphData
        if (!Array.isArray(fullData) || fullData.length === 0) {
            console.warn('Full data is empty; falling back to graphData');
            // Ensure graphData is treated as an array even if it's null/undefined
            fullData = Array.isArray(graphData) ? graphData : [];
        }

        if (!Array.isArray(fullData) || fullData.length === 0) {
            console.error('No data available to download.');
            alert('No data available to download.');
            return;
        }

        // Helper function to get value with fallbacks
        const getValue = (item: any, keys: string[]): any => {
            for (let key of keys) {
                // Added check to ensure item is a valid object before accessing properties
                if (item && typeof item === 'object' && item[key] !== undefined && item[key] !== null) {
                    return item[key];
                }
            }
            return '';
        };

        // Define a helper to get field value with extended fallback keys based on timeframe and potential data structures
        const getValueForField = (item: any, field: string, current_timeframe: "day" | "week" | "month" | "year") => {
            let keysToTry: string[] = [];

            if (current_timeframe === 'day') {
                // For day view, prioritize common graphData keys for hourly view, then fallback to potential raw API keys
                const hourlyKeysMap: Record<string, string[]> = {
                    timestamp: ['date', 'time', 'timestamp', 'hourlyTimestamp'], // Prioritize graphData keys ('date', 'time'), then raw API keys
                    total_vehicles: ['vehicles', 'totalVehicles', 'total_vehicles', 'hourlyTotalVehicles'], // Prioritize graphData key ('vehicles'), then raw/common API keys
                    average_occupancy: ['occupancy', 'averageOccupancy', 'average_occupancy', 'hourlyAverageOccupancy'], // Prioritize graphData key ('occupancy'), then raw/common API keys
                    total_revenue: ['revenue', 'totalRevenue', 'total_revenue', 'hourlyTotalRevenue'], // Prioritize graphData key ('revenue'), then raw/common API keys
                    pending_revenue: ['pendingRevenue', 'pending_revenue', 'hourlyPendingRevenue'], // These might be consistent, but include variations
                    subscriber_revenue: ['subscriptions', 'subscriberRevenue', 'subscriber_revenue', 'hourlySubscriberRevenue'], // Prioritize graphData key ('subscriptions'), then raw/common API keys
                    lost_revenue: ['lostRevenue', 'lost_revenue', 'hourlyLostRevenue'], // Include potential variations
                    peakTime: ['peakHour', 'peakTime', 'hourlyPeakTime'], // Include potential variations
                    hourly_data_id: ['hourly_data_id', 'hourlyDataId', 'dailyDataId'], // Include potential variations
                    lot_id: ['lot_id', 'lotId'], // Include potential variations
                    up_time: ['upTime', 'up_time', 'hourlyUpTime'], // Include potential variations
                    total_registered_vehicles: ['registeredVehicles', 'total_registered_vehicles', 'hourlyTotalRegisteredVehicles'], // Prioritize graphData, then raw/common
                    total_uniregistered_return_vehicles: ['unregisteredReturnVehicles', 'total_uniregistered_return_vehicles', 'hourlyTotalUnregisteredReturnVehicles'], // Prioritize graphData, then raw/common
                    total_new_vehicles: ['newVehicles', 'total_new_vehicles', 'hourlyTotalNewVehicles'], // Prioritize graphData, then raw/common
                    total_violations: ['violations', 'total_violations', 'hourlyTotalViolations'], // Prioritize graphData, then raw/common
                    total_electric_vehicles: ['electricVehicles', 'total_electric_vehicles', 'hourlyTotalElectricVehicles'], // Prioritize graphData, then raw/common
                    electric_vehicle_make_count: ['evMakeCount', 'electric_vehicle_make_count', 'hourlyElectricVehicleMakeCount'], // Prioritize graphData, then raw/common
                    average_vehicle_dwell_time: ['avgVehicleDwellTime', 'average_vehicle_dwell_time', 'hourlyAverageVehicleDwellTime'], // Prioritize graphData, then raw/common
                    average_registered_vehicle_dwell_time: ['avgRegisteredDwellTime', 'average_registered_vehicle_dwell_time', 'hourlyAverageRegisteredVehicleDwellTime'], // Prioritize graphData, then raw/common
                    average_new_visitor_vehicles_dwell_time: ['avgNewVisitorDwellTime', 'average_new_visitor_vehicles_dwell_time', 'hourlyAverageNewVisitorVehiclesDwellTime'], // Prioritize graphData, then raw/common
                    average_return_vehicles_dwell_time: ['avgReturnDwellTime', 'average_return_vehicles_dwell_time', 'hourlyAverageReturnVehiclesDwellTime'], // Prioritize graphData, then raw/common
                    average_electric_vehicles_dwell_time: ['avgElectricDwellTime', 'average_electric_vehicles_dwell_time', 'hourlyAverageElectricVehiclesDwellTime'], // Prioritize graphData, then raw/common
                    average_violator_dwell_time: ['avgViolatorDwellTime', 'average_violator_dwell_time', 'hourlyAverageViolatorDwellTime'], // Prioritize graphData, then raw/common
                };
                keysToTry.push(...hourlyKeysMap[field] || []);

            } else {
                // Original mappings for daily data (week/month/year views)
                const dailyFieldsMap: Record<string, string[]> = {
                    hourly_data_id: ['dailyDataId', 'hourly_data_id'],
                    lot_id: ['lotId', 'lot_id'],
                    timestamp: ['date', 'timestamp'],
                    total_vehicles: ['totalVehicles', 'total_vehicles'],
                    average_occupancy: ['averageOccupancy', 'average_occupancy'],
                    up_time: ['upTime', 'up_time'],
                    total_revenue: ['totalRevenue', 'total_revenue', 'revenue'],
                    pending_revenue: ['pendingRevenue', 'pending_revenue'],
                    subscriber_revenue: ['subscriberRevenue', 'subscriber_revenue', 'subscriptions'],
                    lost_revenue: ['lostRevenue', 'lost_revenue'],
                    peakTime: ['peakHour', 'peakTime'],
                    total_registered_vehicles: ['totalRegisteredVehicles', 'total_registered_vehicles'],
                    total_uniregistered_return_vehicles: ['totalUnregisteredReturnVehicles', 'total_uniregistered_return_vehicles'],
                    total_new_vehicles: ['totalNewVehicles', 'total_new_vehicles'],
                    total_violations: ['totalViolations', 'total_violations'],
                    total_electric_vehicles: ['totalElectricVehicles', 'total_electric_vehicles'],
                    electric_vehicle_make_count: ['electricVehicleMakeCount', 'electric_vehicle_make_count'],
                    average_vehicle_dwell_time: ['averageVehicleDwellTime', 'average_vehicle_dwell_time'],
                    average_registered_vehicle_dwell_time: ['averageRegisteredVehicleDwellTime', 'average_registered_vehicle_dwell_time'],
                    average_new_visitor_vehicles_dwell_time: ['averageNewVisitorVehicleDwellTime', 'average_new_visitor_vehicles_dwell_time'],
                    average_return_vehicles_dwell_time: ['averageReturnVehiclesDwellTime', 'average_return_vehicles_dwell_time'],
                    average_electric_vehicles_dwell_time: ['averageElectricVehiclesDwellTime', 'average_electric_vehicles_dwell_time'],
                    average_violator_dwell_time: ['averageViolatorDwellTime', 'average_violator_dwell_time'],
                };
                keysToTry.push(...dailyFieldsMap[field] || []);
            }

            // Filter out duplicate keys while maintaining order of first appearance
            const uniqueKeysToTry = keysToTry.filter((key, index, self) => self.indexOf(key) === index);

            return getValue(item, uniqueKeysToTry);
        };

        const transformedData = fullData.map(item => ({
            hourly_data_id: getValueForField(item, 'hourly_data_id', timeframe),
            lot_id: getValueForField(item, 'lot_id', timeframe),
            timestamp: getValueForField(item, 'timestamp', timeframe),
            total_vehicles: getValueForField(item, 'total_vehicles', timeframe),
            average_occupancy: getValueForField(item, 'average_occupancy', timeframe),
            up_time: getValueForField(item, 'up_time', timeframe),
            total_revenue: getValueForField(item, 'total_revenue', timeframe),
            pending_revenue: getValueForField(item, 'pending_revenue', timeframe),
            subscriber_revenue: getValueForField(item, 'subscriber_revenue', timeframe),
            lost_revenue: getValueForField(item, 'lost_revenue', timeframe),
            peakTime: getValueForField(item, 'peakTime', timeframe),
            total_registered_vehicles: getValueForField(item, 'total_registered_vehicles', timeframe),
            total_uniregistered_return_vehicles: getValueForField(item, 'total_uniregistered_return_vehicles', timeframe),
            total_new_vehicles: getValueForField(item, 'total_new_vehicles', timeframe),
            total_violations: getValueForField(item, 'total_violations', timeframe),
            total_electric_vehicles: getValueForField(item, 'total_electric_vehicles', timeframe),
            electric_vehicle_make_count: getValueForField(item, 'electric_vehicle_make_count', timeframe),
            average_vehicle_dwell_time: getValueForField(item, 'average_vehicle_dwell_time', timeframe),
            average_registered_vehicle_dwell_time: getValueForField(item, 'average_registered_vehicle_dwell_time', timeframe),
            average_new_visitor_vehicles_dwell_time: getValueForField(item, 'average_new_visitor_vehicles_dwell_time', timeframe),
            average_return_vehicles_dwell_time: getValueForField(item, 'average_return_vehicles_dwell_time', timeframe),
            average_electric_vehicles_dwell_time: getValueForField(item, 'average_electric_vehicles_dwell_time', timeframe),
            average_violator_dwell_time: getValueForField(item, 'average_violator_dwell_time', timeframe)
        }));

        // Define the fixed CSV headers
        const headers = [
            'hourly_data_id',
            'lot_id',
            'timestamp',
            'total_vehicles',
            'average_occupancy',
            'up_time',
            'total_revenue',
            'pending_revenue',
            'subscriber_revenue',
            'lost_revenue',
            'peakTime',
            'total_registered_vehicles',
            'total_uniregistered_return_vehicles',
            'total_new_vehicles',
            'total_violations',
            'total_electric_vehicles',
            'electric_vehicle_make_count',
            'average_vehicle_dwell_time',
            'average_registered_vehicle_dwell_time',
            'average_new_visitor_vehicles_dwell_time',
            'average_return_vehicles_dwell_time',
            'average_electric_vehicles_dwell_time',
            'average_violator_dwell_time'
        ];

        // Process data for both formats
        const processedData = transformedData.map(row => {
            const processedRow: any = {};
            headers.forEach(header => {
                let value = (row as any)[header];
                if ((header === 'timestamp' || header === 'peakTime') && value) {
                    const dateObj = new Date(value);
                    if (!isNaN(dateObj.getTime())) {
                        value = (timeframe === 'day')
                            ? dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })
                            : dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    }
                }
                if (typeof value === 'number') {
                    if (header.toLowerCase().includes('vehicles') || header.toLowerCase().includes('count') || header.toLowerCase().includes('violations')) {
                        value = value.toFixed(0);
                    } else {
                        value = value.toFixed(2);
                    }
                }
                if (value === null || value === undefined) value = '';
                processedRow[header] = value;
            });
            return processedRow;
        });

        if (format === 'csv') {
            // Generate CSV content
            const csvContent = [
                headers.join(','),
                ...processedData.map(row => {
                    return headers.map(header => {
                        const value = String(row[header]).replace(/"/g, '""');
                        return `"${value}"`;
                    }).join(',');
                })
            ].join('\n');

            // Create and trigger CSV download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${filename}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'xl') {
            // For XL format, we'll create a simple Excel-like CSV with better formatting
            // This is a fallback since we don't have a proper Excel library
            // In a real implementation, you'd use a library like 'xlsx' or 'exceljs'
            const xlContent = [
                headers.join('\t'), // Use tab separation for better Excel compatibility
                ...processedData.map(row => {
                    return headers.map(header => {
                        const value = String(row[header]);
                        return value;
                    }).join('\t');
                })
            ].join('\n');

            // Create and trigger XL download (as TSV for now)
            const blob = new Blob([xlContent], { type: 'text/tab-separated-values;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${filename}.xls`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        console.log('Download triggered for file:', filename, 'format:', format);
        setIsDownloadModalOpen(false);
    };

    return (
        <div className="dashboard-header">
            <div className="header-title-row">
                <h1>
                    <span className="dashboard-text">Dashboard</span>
                    <span className="lot-name">{displayLotName}</span>
                    {isLoading && (<span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0.5rem' }}><span style={{ display: 'inline-block' }}><LoadingWheelSmall /></span></span>)}
                </h1>
                <div className="header-buttons">
                    <button
                        onClick={handleDownloadData}
                        className="button-secondary"
                        data-walkthrough="download-data"
                    >
                        <img src="/assets/Download.svg" alt="Download Data" />
                        Download Data
                    </button>
                </div>
            </div>

            <div className="controls-row">
                <div
                    className="analytics-selector"
                    data-active={currentAnalytics}
                    data-walkthrough="analytics-selector"
                    ref={analyticsRef}
                >
                    <div className="active-pill"></div>
                    {analyticsOptions.map((option) => (
                        <button
                            key={option.id}
                            className={`${currentAnalytics === option.id ? "active" : ""} ${option.id}`}
                            onClick={() => handleAnalyticsChange(option.id)}
                        >
                            <img
                                src={`/assets/dashboard/${currentAnalytics === option.id ? 'Selected' : 'Unselected'}_${option.id === 'dwell-time' ? 'DwellTime' : option.id.charAt(0).toUpperCase() + option.id.slice(1)}.svg`}
                                alt={option.label}
                            />
                            <span className="analytics-label">{option.label}</span>
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <div className="mobile-analytics-dropdown">
                        <div className="menu-wrapper">
                            <Menu
                                trigger={
                                    <button className="mobile-analytics-dropdown-trigger" type="button">
                                        <img
                                            src={getAnalyticsIcon(currentAnalytics, true)}
                                            alt={currentAnalytics}
                                        />
                                        <span className="mobile-analytics-dropdown-label">
                                            {analyticsOptions.find(opt => opt.id === currentAnalytics)?.label}
                                        </span>
                                        <img
                                            src="/assets/ArrowDropdown.svg"
                                            alt="Dropdown"
                                            className={`mobile-dropdown-arrow ${isAnalyticsMenuOpen ? 'open' : ''}`}
                                        />
                                    </button>
                                }
                                items={analyticsMenuItems}
                                placement="bottom-end"
                                dropdownClassName="mobile-analytics-dropdown-menu"
                                onOpenChange={setIsAnalyticsMenuOpen}
                                allowNoSelection={false}
                            />
                        </div>
                    </div>

                    <div
                        className="timeframe-selector"
                        data-active={timeframe}
                        data-walkthrough="timeframe-selector"
                        ref={timeframeRef}
                    >
                        <div className="active-pill"></div>
                        {[
                            { key: "day", label: isSmallScreen ? "D" : "Day" },
                            { key: "week", label: isSmallScreen ? "W" : "Week" },
                            { key: "month", label: isSmallScreen ? "M" : "Month" },
                            { key: "year", label: isSmallScreen ? "Y" : "Year" }
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                className={timeframe === key ? "active" : ""}
                                onClick={() => {
                                    setTimeframe(key as any);
                                    if (showToday) {
                                        setSelectedDate(new Date());
                                    }
                                }}
                                disabled={isLoading}
                            >
                                <span className="timeframe-label">{label}</span>
                            </button>
                        ))}
                        <button
                            onClick={handleDownloadData}
                            className="download-data-button"
                            style={{
                                marginLeft: '8px',
                                display: 'none'
                            }}
                        >
                            <img src="/assets/Download.svg" alt="Download Data" />
                        </button>
                    </div>
                </div>

                <div
                    className="date-search"
                    onClick={() => setIsTimePickerOpen(true)}
                >
                    <img src="/assets/SearchBarIcon.svg" alt="Search" />
                    <span>{getFormattedDateRange(timeframe)}</span>
                    <div className="date-navigation">
                        <img
                            src="/assets/dashboard/ArrowLeft.svg"
                            alt="Previous"
                            className="nav-arrow"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateDate('backward');
                            }}
                        />
                        <img
                            src="/assets/dashboard/ArrowRight.svg"
                            alt="Next"
                            className={`nav-arrow ${!canNavigateForward ? 'disabled' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (canNavigateForward) {
                                    navigateDate('forward');
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <TimePickerModal
                isOpen={isTimePickerOpen}
                onClose={() => setIsTimePickerOpen(false)}
                timeframe={timeframe}
                onSelect={handleDateSelect}
                selectedDate={selectedDate}
            />
            <DownloadFilenameModal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onConfirm={handleDownloadConfirm}
                defaultFilename={getDefaultDownloadFilename()}
                isLoading={isLoading}
            />
            <Modal
                isOpen={isNoDataModalOpen}
                title="No Data Available"
                description="Please try selecting a different date/time range."
                confirmText=""
                cancelText="Return"
                onConfirm={() => { }}
                onCancel={() => setIsNoDataModalOpen(false)}
                disableConfirm={true}
            />
        </div>
    );
};

export default DashboardHeader;
